/* =============================================================================
   Collection — interactions
   - `<facet-filters-form>` custom element: auto-submits filter changes & sort
     selections to the current page with merged URL params (Shopify pattern).
   - Filter pill popovers: open one at a time; close on outside click + ESC.
   - Mobile drawer: open from "Filter" button, close on backdrop / X / ESC.
   - Load more: hijacks the crawlable `?page=N` anchor, fetches the next page
     via the Section Rendering API, appends new product cards in place, updates
     the visible "Showing X of Y" count + aria-live announcer.
   ========================================================================= */

(() => {
  'use strict';

  /* ── Shared utilities ──────────────────────────────────────────────────── */
  function debounce(fn, wait) {
    let t;
    return function (...args) {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  /* ── <facet-filters-form> ──────────────────────────────────────────────── */
  if (!customElements.get('facet-filters-form')) {
    class FacetFiltersForm extends HTMLElement {
      constructor() {
        super();
        this.debouncedOnSubmit = debounce(this.onSubmit.bind(this), 300);
      }

      connectedCallback() {
        const form = this.querySelector('form');
        if (!form) return;

        form.addEventListener('input', this.debouncedOnSubmit);
        form.addEventListener('change', this.debouncedOnSubmit);
        form.addEventListener('submit', (e) => {
          e.preventDefault();
          this.onSubmit();
        });
      }

      onSubmit() {
        const form = this.querySelector('form');
        if (!form) return;

        const formData = new FormData(form);
        const params = new URLSearchParams();

        for (const [name, value] of formData.entries()) {
          if (value !== '' && value != null) {
            params.append(name, value);
          }
        }

        const url = `${window.location.pathname}?${params.toString()}`;
        window.location.assign(url);
      }
    }

    customElements.define('facet-filters-form', FacetFiltersForm);
  }

  /* ── Filter pill popovers ──────────────────────────────────────────────── */
  function initFilterPopovers(root = document) {
    const filters = root.querySelectorAll('[data-collection-filter]:not([data-popover-initialized])');

    filters.forEach((filter) => {
      // Drawer-context filters render inline — skip the popover wiring.
      if (filter.classList.contains('collection-filter--drawer')) return;

      const button = filter.querySelector('[data-collection-filter-trigger]');
      const panel = filter.querySelector('[data-collection-filter-panel]');
      if (!button || !panel) return;

      filter.dataset.popoverInitialized = 'true';

      button.addEventListener('click', (e) => {
        e.stopPropagation();
        const expanded = button.getAttribute('aria-expanded') === 'true';

        // Close any other open popovers first
        closeAllPopovers(filter);

        if (expanded) {
          closePopover(filter, button, panel);
        } else {
          openPopover(button, panel);
        }
      });

      // Prevent clicks inside the panel from bubbling up to the outside-close handler
      panel.addEventListener('click', (e) => e.stopPropagation());
    });
  }

  function openPopover(button, panel) {
    button.setAttribute('aria-expanded', 'true');
    panel.hidden = false;
  }

  function closePopover(filter, button, panel) {
    button.setAttribute('aria-expanded', 'false');
    panel.hidden = true;
  }

  function closeAllPopovers(except = null) {
    document.querySelectorAll('[data-collection-filter]').forEach((filter) => {
      if (filter === except) return;
      if (filter.classList.contains('collection-filter--drawer')) return;
      const button = filter.querySelector('[data-collection-filter-trigger]');
      const panel = filter.querySelector('[data-collection-filter-panel]');
      if (!button || !panel) return;
      if (button.getAttribute('aria-expanded') === 'true') {
        closePopover(filter, button, panel);
      }
    });
  }

  // Outside-click + ESC handler
  document.addEventListener('click', () => closeAllPopovers());
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      closeAllPopovers();
      closeDrawer();
    }
  });

  /* ── Mobile drawer ─────────────────────────────────────────────────────── */
  let drawerLastFocus = null;

  function openDrawer() {
    const drawer = document.querySelector('[data-collection-filter-drawer]');
    if (!drawer) return;
    drawerLastFocus = document.activeElement;
    drawer.hidden = false;
    requestAnimationFrame(() => drawer.setAttribute('data-open', ''));
    document.body.style.overflow = 'hidden';

    const button = document.querySelector('[data-collection-filter-button]');
    if (button) button.setAttribute('aria-expanded', 'true');

    const close = drawer.querySelector('[data-collection-filter-drawer-close]');
    if (close) close.focus();
  }

  function closeDrawer() {
    const drawer = document.querySelector('[data-collection-filter-drawer]');
    if (!drawer || drawer.hidden) return;
    drawer.removeAttribute('data-open');
    document.body.style.overflow = '';

    const button = document.querySelector('[data-collection-filter-button]');
    if (button) button.setAttribute('aria-expanded', 'false');

    // Wait for the transform to finish before hiding so the animation runs out
    setTimeout(() => {
      drawer.hidden = true;
      if (drawerLastFocus) drawerLastFocus.focus();
    }, 200);
  }

  function initDrawer() {
    const button = document.querySelector('[data-collection-filter-button]');
    if (button) {
      button.addEventListener('click', (e) => {
        e.stopPropagation();
        openDrawer();
      });
    }

    const backdrop = document.querySelector('[data-collection-filter-drawer-backdrop]');
    if (backdrop) backdrop.addEventListener('click', closeDrawer);

    const close = document.querySelector('[data-collection-filter-drawer-close]');
    if (close) close.addEventListener('click', closeDrawer);
  }

  /* ── Toggle filter: keep input in sync with the visual track ──────────── */
  function initToggles(root = document) {
    const toggles = root.querySelectorAll('[data-collection-toggle] input[type=checkbox]:not([data-toggle-initialized])');
    toggles.forEach((input) => {
      input.dataset.toggleInitialized = 'true';
      const wrapper = input.closest('[data-collection-toggle]');
      const sync = () => {
        if (input.checked) wrapper.classList.add('collection-toggle--on');
        else wrapper.classList.remove('collection-toggle--on');
      };
      input.addEventListener('change', sync);
      sync();
    });
  }

  /* ── Load more (progressive enhancement) ───────────────────────────────── */
  function initLoadMore(root = document) {
    const wrappers = root.querySelectorAll(
      '[data-collection-grid-wrapper]:not([data-load-more-initialized])'
    );

    wrappers.forEach((wrapper) => {
      wrapper.dataset.loadMoreInitialized = 'true';

      const sectionId = wrapper.dataset.sectionId;
      const grid = wrapper.querySelector('[data-collection-grid]');
      const button = wrapper.querySelector('[data-collection-load-more]');
      const announcer = wrapper.querySelector('[data-collection-status-announcer]');
      const errorEl = wrapper.querySelector('[data-collection-load-more-error]');
      const retryBtn = wrapper.querySelector('[data-collection-load-more-retry]');

      // The visible "Showing X of Y" count lives in the toolbar, not in the grid wrapper.
      const loadedCountEl = document.querySelector('[data-collection-count-loaded]');
      const totalCountEl = document.querySelector('[data-collection-count-total]');

      let isLoading = false;
      let abortController = null;

      const setLoading = (loading) => {
        isLoading = loading;
        if (!button) return;
        if (loading) {
          button.setAttribute('aria-busy', 'true');
          button.dataset.loading = '';
          if (announcer) announcer.textContent = 'Loading more products…';
        } else {
          button.removeAttribute('aria-busy');
          delete button.dataset.loading;
        }
      };

      const showError = () => {
        if (errorEl) errorEl.hidden = false;
        if (announcer) {
          announcer.textContent = 'Couldn’t load more products. Try again.';
        }
      };

      const hideError = () => {
        if (errorEl) errorEl.hidden = true;
      };

      const updateCount = (loaded, total) => {
        if (loadedCountEl) loadedCountEl.textContent = String(loaded);
        if (totalCountEl) totalCountEl.textContent = String(total);
        wrapper.dataset.loadedCount = String(loaded);
        wrapper.dataset.totalCount = String(total);
      };

      const announce = (message) => {
        if (!announcer) return;
        // Clear + re-set forces VoiceOver to announce identical strings reliably.
        announcer.textContent = '';
        requestAnimationFrame(() => {
          announcer.textContent = message;
        });
      };

      const fetchNext = async () => {
        if (!button || isLoading) return;
        const href = button.getAttribute('href');
        if (!href) return;

        hideError();
        setLoading(true);

        // Append the Section Rendering API param so Shopify returns just our section.
        const url = new URL(href, window.location.origin);
        url.searchParams.set('section_id', sectionId);

        if (abortController) abortController.abort();
        abortController = new AbortController();

        try {
          const response = await fetch(url.toString(), {
            signal: abortController.signal,
            headers: { Accept: 'text/html' },
          });
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          const html = await response.text();

          const doc = new DOMParser().parseFromString(html, 'text/html');
          const newGrid = doc.querySelector('[data-collection-grid]');
          const newWrapper = doc.querySelector('[data-collection-grid-wrapper]');
          const newButton = doc.querySelector('[data-collection-load-more]');
          if (!newGrid || !newWrapper) throw new Error('Malformed section response');

          // Move items out of the fetched grid and append to ours.
          const newItems = Array.from(newGrid.children);
          const fragment = document.createDocumentFragment();
          newItems.forEach((item) => fragment.appendChild(item));
          grid.appendChild(fragment);

          // Update href to next page (or remove button if no further pages).
          if (newButton && newButton.getAttribute('href')) {
            button.setAttribute('href', newButton.getAttribute('href'));
          } else {
            button.remove();
          }

          // Update counts from the server response (authoritative).
          const newTotal = parseInt(newWrapper.dataset.totalCount, 10) || 0;
          const currentLoaded = (parseInt(wrapper.dataset.loadedCount, 10) || 0) + newItems.length;
          updateCount(currentLoaded, newTotal);

          announce(`${newItems.length} more products loaded. Showing ${currentLoaded} of ${newTotal}.`);

          // Notify other listeners (e.g. analytics) that the grid grew.
          wrapper.dispatchEvent(
            new CustomEvent('collection:loadmore', {
              bubbles: true,
              detail: { appended: newItems.length, loaded: currentLoaded, total: newTotal },
            })
          );
        } catch (err) {
          if (err.name === 'AbortError') return;
          showError();
        } finally {
          setLoading(false);
        }
      };

      if (button) {
        button.addEventListener('click', (e) => {
          e.preventDefault();
          fetchNext();
        });
      }

      if (retryBtn) {
        retryBtn.addEventListener('click', () => fetchNext());
      }
    });
  }

  /* ── Boot ──────────────────────────────────────────────────────────────── */
  function boot() {
    initFilterPopovers();
    initDrawer();
    initToggles();
    initLoadMore();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-init on theme-editor section reload
  document.addEventListener('shopify:section:load', (e) => {
    if (e.target && e.target.querySelector('[data-collection-section]')) {
      boot();
    }
  });
})();
